// Step 6: Review & Activate

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  Check, 
  AlertCircle, 
  Building2, 
  CreditCard, 
  Workflow, 
  FileText, 
  Clock,
  Rocket,
  Shield,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { WizardStepProps } from '@/lib/onboarding/types';
import { useClientOnboarding } from '@/hooks/useClientOnboarding';
import { useClientSettings } from '@/hooks/useClientSettings';
import { supabase } from '@/integrations/supabase/client';

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  complete: boolean;
  children: React.ReactNode;
}

function ReviewSection({ title, icon, complete, children }: SectionProps) {
  const [open, setOpen] = useState(true);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 rounded-lg">
        <div className="flex items-center gap-3">
          <div className={cn(
            'p-2 rounded-full',
            complete ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
          )}>
            {icon}
          </div>
          <span className="font-medium">{title}</span>
          {complete ? (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <Check className="mr-1 h-3 w-3" />
              Complete
            </Badge>
          ) : (
            <Badge variant="destructive">Incomplete</Badge>
          )}
        </div>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">
        <div className="ml-11 space-y-2">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function Step6ReviewActivate({ clientId, progress, onComplete, onBack }: WizardStepProps) {
  const { activateClient, isActivating, snapshots } = useClientOnboarding(clientId);
  const { settings } = useClientSettings(clientId);
  
  const [activationNotes, setActivationNotes] = useState('');
  const [clientData, setClientData] = useState<{ client_name: string; client_type: string; primary_state: string | null } | null>(null);

  // Load client data
  useEffect(() => {
    async function loadClient() {
      const { data } = await supabase
        .from('clients')
        .select('client_name, client_type, primary_state')
        .eq('id', clientId)
        .single();
      if (data) setClientData(data);
    }
    loadClient();
  }, [clientId]);

  const canActivate = 
    progress.step1_complete && 
    progress.step2_complete && 
    progress.step3_complete && 
    progress.step4_complete && 
    progress.step5_complete;

  const isAlreadyActivated = progress.step6_complete || snapshots.length > 0;

  const handleActivate = () => {
    activateClient(activationNotes || undefined, {
      onSuccess: onComplete,
    });
  };

  const totalPlans = progress.selected_payers.reduce(
    (acc, p) => acc + p.plans.length, 
    0
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Rocket className="h-6 w-6" />
          Review & Activate
        </CardTitle>
        <CardDescription>
          Review all configuration settings before activating this client for live operations.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isAlreadyActivated && (
          <Alert className="border-green-500 bg-green-50">
            <Shield className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">Client Activated</AlertTitle>
            <AlertDescription className="text-green-700">
              This client was activated on {snapshots[0] && format(new Date(snapshots[0].activated_at), 'PPP')}. 
              Configuration is locked. Contact support for changes.
            </AlertDescription>
          </Alert>
        )}

        {/* Step 1: Client Profile */}
        <ReviewSection
          title="Client Profile"
          icon={<Building2 className="h-4 w-4" />}
          complete={progress.step1_complete}
        >
          <div className="grid gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name:</span>
              <span className="font-medium">{clientData?.client_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <span>{clientData?.client_type === 'HOSPITAL' ? 'Hospital' : 'Surgical Practice'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">State:</span>
              <span>{clientData?.primary_state || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Contact:</span>
              <span>{progress.primary_contact_name} ({progress.primary_contact_email})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Go-Live Target:</span>
              <span>{progress.go_live_target_date ? format(new Date(progress.go_live_target_date), 'PPP') : 'Not set'}</span>
            </div>
          </div>
        </ReviewSection>

        <Separator />

        {/* Step 2: Payers */}
        <ReviewSection
          title="Payer Configuration"
          icon={<CreditCard className="h-4 w-4" />}
          complete={progress.step2_complete}
        >
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              {progress.selected_payers.length} payers, {totalPlans} plans configured
            </div>
            {progress.selected_payers.map(payer => (
              <div key={payer.payer_id} className="p-2 bg-muted/50 rounded">
                <div className="font-medium text-sm">{payer.payer_name}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {payer.plans.map(plan => (
                    <Badge key={plan.plan_id} variant="outline" className="text-xs">
                      {plan.plan_type}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ReviewSection>

        <Separator />

        {/* Step 3: Epic */}
        <ReviewSection
          title="Epic Integration"
          icon={<Workflow className="h-4 w-4" />}
          complete={progress.step3_complete}
        >
          <div className="grid gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Environment:</span>
              <Badge variant={progress.epic_environment === 'none' ? 'secondary' : 'default'}>
                {progress.epic_environment === 'none' ? 'Not Connected' : 
                 progress.epic_environment === 'sandbox' ? 'Sandbox' : 'Production'}
              </Badge>
            </div>
            {progress.epic_environment !== 'none' && (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pushback:</span>
                  <span>{progress.epic_pushback_enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                {progress.epic_pushback_enabled && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Destinations:</span>
                    <span>{progress.epic_pushback_destinations.join(', ')}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mapping Validated:</span>
                  <span>{progress.epic_mapping_validated ? 'Yes' : 'No'}</span>
                </div>
              </>
            )}
          </div>
        </ReviewSection>

        <Separator />

        {/* Step 4: Rulesets */}
        <ReviewSection
          title="Ruleset Templates"
          icon={<FileText className="h-4 w-4" />}
          complete={progress.step4_complete}
        >
          <div className="space-y-2 text-sm">
            {progress.selected_payers.flatMap(payer => 
              payer.plans.map(plan => (
                <div key={plan.plan_id} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {payer.payer_name} - {plan.plan_type}:
                  </span>
                  <span>{plan.selected_ruleset_template || 'Not set'}</span>
                </div>
              ))
            )}
          </div>
        </ReviewSection>

        <Separator />

        {/* Step 5: SLA */}
        <ReviewSection
          title="SLA & Follow-Up"
          icon={<Clock className="h-4 w-4" />}
          complete={progress.step5_complete}
        >
          <div className="grid gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Default SLA:</span>
              <span>{settings?.sla_hours_default || 72} hours</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Urgent SLA:</span>
              <span>{settings?.urgent_sla_hours_default || 24} hours</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Follow-Up Cadence:</span>
              <span>{settings?.follow_up_cadence_hours_default || 48} hours</span>
            </div>
          </div>
        </ReviewSection>

        <Separator />

        {/* Activation */}
        {!isAlreadyActivated && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="activation-notes">Activation Notes (Optional)</Label>
              <Textarea
                id="activation-notes"
                placeholder="Any notes about this activation..."
                value={activationNotes}
                onChange={(e) => setActivationNotes(e.target.value)}
                rows={3}
              />
            </div>

            {!canActivate && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Cannot Activate</AlertTitle>
                <AlertDescription>
                  All steps must be completed before activating this client.
                  Please complete the incomplete steps above.
                </AlertDescription>
              </Alert>
            )}

            <Alert>
              <Shield className="h-4 w-4" />
              <AlertTitle>Activation Creates Immutable Snapshot</AlertTitle>
              <AlertDescription>
                Once activated, a configuration snapshot will be created for audit purposes.
                This client will be able to create live authorization cases.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack} disabled={isAlreadyActivated}>
            Back
          </Button>
          {!isAlreadyActivated && (
            <Button 
              onClick={handleActivate}
              disabled={!canActivate || isActivating}
              className="bg-green-600 hover:bg-green-700"
            >
              {isActivating ? (
                'Activating...'
              ) : (
                <>
                  <Rocket className="mr-2 h-4 w-4" />
                  Activate Client
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
